<template>
  <div>
    <wc-dialog
      :showDialog='showDialog'
      :title='title'
      :icon='division.icon'
      iconColor='cyan'
      @cancel="$emit('cancel')"
      @save='create'
      width='400'
      :loadingButtons='isLoadingContacts || isCreating'
      :disableButtons='!isFormValid'
    >
      <v-form v-model='isFormValid' ref='form'>
        <v-text-field
          label='Estimate name'
          :rules='[rules.required]'
          v-model='newEstimate.name'
          :hint='projectName.hint'
          persistent-hint
          :append-outer-icon='projectName.icon'
          @click:append-outer='useOwnerName'
        ></v-text-field>

        <v-select
          v-if="division.division_type === 'I'"
          label='Estimate Type'
          :items='estimateTypes'
          v-model='newEstimate.estimate_type'
        ></v-select>
        <v-autocomplete
          v-if='!disableProjectPicker'
          :disabled="disableProjectPicker"
          label='For Project'
          :rules="newEstimate.estimate_type === 'SE' ? [rules.required] : []"
          :items='projectList'
          v-model='projectID'
        ></v-autocomplete>
        <client-form-search
          ref='clientPicker'
          v-model='newEstimate.client'
          label='Select a Client'
          showAdd
          :initialData="showContactReference ? contactReference : null"
        ></client-form-search>
          
        <div v-if='showContactReference'>
          <br>
          <wc-data-box mini title='Contact Reference'>
            <v-card-text>
              <span v-if='contactReference.name'>{{ contactReference.name }} {{ contactReference.last_name }}</span>
              <span v-if='contactReference.cell_phone'><br>{{ contactReference.cell_phone }}</span>
              <span v-if='contactReference.email'><br>{{ contactReference.email }}</span>
              <br>
              <small>This contact will auto-fill the Quick Create if you click + above</small>
            </v-card-text>
          </wc-data-box>
          <br>
        </div>

        <div v-if='showSiteAddress && disableProjectPicker'>
          <v-text-field
            label='Address'
            v-model='newEstimate.site_address'
          ></v-text-field>
          <v-text-field
            label='City'
            v-model='newEstimate.site_city'
          ></v-text-field>
        </div>
      </v-form>
    </wc-dialog>

    <wc-quick-create-client
      ref='quickCreateDialog'
      :showQuickCreate='showQuickCreate'
      :initialData="showContactReference ? contactReference : null"
      @cancel='showQuickCreate = false'
      @save='saveQuickCreate'
      @updateClient='chooseClient'
    ></wc-quick-create-client>
  </div>
</template>

<script>
import ClientFormSearch from '@/modules/clients/components/ClientFormSearch';
import WCQuickCreateClient from '@/components/clients/WCQuickCreateClient';
import formRules from '@/mixins/formRules';
import estimateOptionsAPI from '@/modules/estimates/api/estimateOptions';
import estimatesAPI from '@/modules/estimates/api/estimates';
import _ from 'lodash';

export default {
  name: 'new-estimate-setup',
  mixins: [formRules],
  data: () => ({
    isLoading: false,
    isCreating: false,
    isFormValid: false,
    isLoadingContacts: false,
    savingQuickCreate: false,
    showQuickCreate: false,
    clients: [],
    newEstimate: {},
    projectID: null,
    resetEstimate: {
      division: null,
      template: null,
      name: '',
      client: null,
      estimator: null,
      estimate_type: ''
    }, 
  }),
  props: {
    showDialog: {
      required: true
    },
    division: {
      required: true
    },
    projects: {
      required: true
    },
    siteReference: {
      required: false
    },
    contactReference: {
      required: false
    },
    showSiteAddress: {
      required: false,
      type: Boolean,
      default: false
    },
    showContactReference: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  watch: {
    showDialog () {
      if (this.showDialog) {
        this.newEstimate = _.cloneDeep(this.resetEstimate)
        this.newEstimate.division = this.division.id
        if (this.division.division_type === 'I') {
          if (this.showSiteAddress) {
            this.newEstimate.estimate_type = 'ST'
          } else {
            this.newEstimate.estimate_type = 'NI'
          }
        }
        if (this.showSiteAddress) {
          this.newEstimate.site_address = this.siteReference.address
          this.newEstimate.site_city = this.siteReference.city
        }
        setTimeout(() => {
          this.$refs.form.resetValidation()
        }, 100)
      }
    }
  },
  computed: {
    divisions () {
      return this.$store.state.misc.divisions
    },
    title () {
      if (this.division) {
        return 'New ' + this.division.division + ' Estimate'
      }
      return ''
    },
    estimateTypes () {
      return this.$store.state.misc.globalOptions.estimateTypeOptions
    },
    projectList () {
      let data = []
      if (this.projects.length) {
        this.projects.forEach(p => {
          data.push({
            value: p.id,
            text: p.name + ' - ' + p.site_address + ' ' + p.site_city
          })
        })
      }
      return data
    },
    projectName () {
      if (this.newEstimate.client) {
        return { hint: "Click avatar to use owner's name", icon: "mdi-account" }
      } else if (!this.newEstimate.client && this.newEstimate.name) {
        return { hint: "Click avatar to delete project name", icon: "mdi-account-cancel" }
      }
      return { hint: "", icon: "" }
    },
    disableProjectPicker () {
      // If there's no division set in newEstimate, then skip all checks and return True -- disable the picker
      if ('division' in this.newEstimate) {
        let div = this.divisions.find(d => d.id === this.newEstimate.division)
        // If it's not an irrigation division, again, disable the picker
        if (div) {
          if (div.division_type !== 'I') {
            return true
          } else {
            // The only time we SHOW the picker is for service on Existing systems
            if (this.newEstimate.estimate_type === 'SE') {
              return false
            } else {
              return true
            }
          }
        }
      }
      return true
    }
  },
  methods: {
    useOwnerName () {
      if (this.newEstimate.client) {
        const client = this.$refs.clientPicker.$data.selected
        if (client.is_company && !client.name) {
          alert('Error - No Company name has been set!')
          return
        } else if (!client.is_company && !client.last_name) {
          alert("Error - Client's last name has been set!")
          return
        }
        let newName = client.is_company ? client.name : client.last_name
        this.newEstimate.name = newName
      } else {
        this.newEstimate.name = ""
        this.$refs.form.resetValidation()
      }
    },
    async create () {
      this.isCreating = true;
      if (this.newEstimate.client) {
        this.newEstimate.owner = this.newEstimate.client;
        this.newEstimate.bill_to = this.newEstimate.client;
        this.newEstimate.install_contact = this.newEstimate.client;
        this.newEstimate.sales_contact = this.newEstimate.client;
        this.newEstimate.notices_contact = this.newEstimate.client;
        this.newEstimate.site_contact = this.newEstimate.client;
        this.newEstimate.secondary_site_contact = this.newEstimate.client;
        this.newEstimate.estimator = this.$store.state.auth.profile.id;
      }
      let est = await estimatesAPI.createEstimate(this.newEstimate);
      if (est) {
      // Now we determine the estimate type, and create the estimate detail object next.
        let det = { estimate: est.id }
        switch (est.division_obj.division_type) {
          case 'S':
            // det.driveway_rating = null
            await estimatesAPI.createSnowEstimate(det)
            break
          case 'I':
            await estimatesAPI.createIrrigationEstimate(det)
            break
          case 'L':
            await estimatesAPI.createLawnEstimate(det)
            break
        }
        if (est.estimate_type == 'ST') {
          // System Takeovers start with a default set of options
          let line_item = {
            estimate: est.id,
            description: 'Tag Shutoff',
            time_estimate: 5,
            product_estimate: 0
          }
          await estimateOptionsAPI.createEstimateOption(line_item)
          line_item = {
            estimate: est.id,
            description: 'Add Sticker to Controller',
            time_estimate: 5,
            product_estimate: 0
          }
          await estimateOptionsAPI.createEstimateOption(line_item)
          line_item = {
            estimate: est.id,
            description: 'Tape valves',
            time_estimate: 10,
            product_estimate: 0
          }
          await estimateOptionsAPI.createEstimateOption(line_item)
          line_item = {
            estimate: est.id,
            description: 'Asbuilt Sketch (includes blowdown connection, valves, zones, heads)',
            time_estimate: 45,
            product_estimate: 0
          }
          await estimateOptionsAPI.createEstimateOption(line_item)
          line_item = {
            estimate: est.id,
            description: 'Add exterior isolation valve',
            time_estimate: 60,
            product_estimate: 0
          }
          await estimateOptionsAPI.createEstimateOption(line_item)
        }
        if (this.newEstimate.estimate_type == 'SE') {
          est.project = this.projectID
          est = await estimatesAPI.updateEstimate(est) 
        }
        this.$emit('estimateCreated', est)
        this.openEstimate(est)
      } else {
        this.isCreating = false
        this.$emit('cancel')
      }
    },
    cancelAdd () {
      this.$refs.form.reset()
      this.$refs.clientPicker.clearResults()
      this.showAdd = false
    },
    quickCreate () {
      this.showQuickCreate = true
      this.$refs.quickCreateDialog.resetForm()
    },
    openEstimate (est) {
      // this command requires Division Specific Estimate Detail Pages to have a common naming convention (which they should/will)
      this.$router.push({ name: est.division_obj.division + 'Estimate', params: { id: est.id } })
    },
    async saveQuickCreate (newClient) {
      this.clients.push(newClient);
      this.clients = _.orderBy(this.clients, 'name_display');
      this.newEstimate.client = newClient.id;
      this.useOwnerName();
      this.showQuickCreate = false;
    },
    chooseClient(client){
      console.log(JSON.parse(JSON.stringify(client)))
    }
  },
  components: {
    'client-form-search': ClientFormSearch,
    'wc-quick-create-client': WCQuickCreateClient
  }
}
</script>
